import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle, XCircle, Clock, User, Wrench, Trash2, Package, ArrowRight, Truck, Calendar, Building } from 'lucide-react';
import { ConfirmDialog } from '@/components/ConfirmDialog';

const ToolIssues = () => {
  const { user } = useContext(AuthContext);
  const [issues, setIssues] = useState([]);
  const [tools, setTools] = useState([]);
  const [defectiveTools, setDefectiveTools] = useState([]);
  const [leasingReturns, setLeasingReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [selectedReplacementTool, setSelectedReplacementTool] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteIssueId, setDeleteIssueId] = useState(null);
  const [confirmDeleteDefectiveOpen, setConfirmDeleteDefectiveOpen] = useState(false);
  const [deleteDefectiveId, setDeleteDefectiveId] = useState(null);
  const [confirmReturnOpen, setConfirmReturnOpen] = useState(false);
  const [returnId, setReturnId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [issuesRes, toolsRes, defectiveRes, leasingRes] = await Promise.all([
        axios.get(`${API}/tool-issues`),
        axios.get(`${API}/tools`),
        axios.get(`${API}/defective-tools`).catch(() => ({ data: [] })),
        axios.get(`${API}/tool-issues/leasing-returns`).catch(() => ({ data: [] }))
      ]);
      setIssues(issuesRes.data);
      setTools(toolsRes.data);
      setDefectiveTools(defectiveRes.data);
      setLeasingReturns(leasingRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDefective = (defectiveId) => {
    setDeleteDefectiveId(defectiveId);
    setConfirmDeleteDefectiveOpen(true);
  };

  const confirmDeleteDefective = async () => {
    if (!deleteDefectiveId) return;
    try {
      await axios.delete(`${API}/defective-tools/${deleteDefectiveId}`);
      toast.success('Entrée supprimée de la liste');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setConfirmDeleteDefectiveOpen(false);
      setDeleteDefectiveId(null);
    }
  };

  const handleCompleteReturn = (id) => {
    setReturnId(id);
    setConfirmReturnOpen(true);
  };

  const confirmCompleteReturn = async () => {
    if (!returnId) return;
    try {
      await axios.post(`${API}/tool-issues/leasing-returns/${returnId}/complete`);
      toast.success('Restitution au fournisseur enregistrée');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la restitution');
    } finally {
      setConfirmReturnOpen(false);
      setReturnId(null);
    }
  };

  const handleProcess = async (action) => {
    if (!selectedIssue) return;
    
    // If approving non-leasing issue, require a replacement tool
    if (action === 'processed' && selectedIssue.issue_type !== 'leasing_end' && !selectedReplacementTool) {
      toast.error('Veuillez sélectionner un outil de remplacement');
      return;
    }
    
    try {
      await axios.put(`${API}/tool-issues/${selectedIssue.id}/process`, {
        action: action,
        admin_notes: adminNotes || null,
        new_tool_id: (action === 'processed' && selectedIssue.issue_type !== 'leasing_end') ? selectedReplacementTool : null
      });
      
      if (selectedIssue.issue_type === 'leasing_end' && action === 'processed') {
        toast.success('Fin de leasing traitée - Outil prêt à retourner au fournisseur');
      } else {
        toast.success(action === 'processed' ? 'Signalement traité - Remplacement effectué' : 'Signalement rejeté');
      }
      
      setProcessDialogOpen(false);
      setSelectedIssue(null);
      setAdminNotes('');
      setSelectedReplacementTool('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du traitement');
    }
  };

  const handleDelete = (issueId) => {
    setDeleteIssueId(issueId);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteIssueId) return;
    try {
      await axios.delete(`${API}/tool-issues/${deleteIssueId}`);
      toast.success('Signalement supprimé');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setConfirmDeleteOpen(false);
      setDeleteIssueId(null);
    }
  };

  const openProcessDialog = (issue) => {
    setSelectedIssue(issue);
    setAdminNotes('');
    setSelectedReplacementTool('');
    setProcessDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getIssueTypeLabel = (type) => {
    const labels = {
      lost: 'Perdu',
      broken: 'Cassé',
      worn: 'Usé',
      leasing_end: 'Fin de leasing',
    };
    return labels[type] || type;
  };

  const getIssueTypeBadge = (type) => {
    const variants = {
      lost: 'destructive',
      broken: 'destructive',
      worn: 'secondary',
      leasing_end: 'outline',
    };
    const colors = {
      leasing_end: 'border-blue-500 text-blue-600 bg-blue-50',
    };
    return (
      <Badge variant={variants[type] || 'outline'} className={colors[type] || ''}>
        {type === 'leasing_end' && <Truck className="w-3 h-3 mr-1" />}
        {getIssueTypeLabel(type)}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'En attente', variant: 'outline', icon: Clock },
      processed: { label: 'Traité', variant: 'default', icon: CheckCircle },
      rejected: { label: 'Rejeté', variant: 'secondary', icon: XCircle },
    };
    const { label, variant, icon: Icon } = config[status] || { label: status, variant: 'outline', icon: Clock };
    return (
      <Badge variant={variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const pendingIssues = issues.filter(i => i.status === 'pending');
  const processedIssues = issues.filter(i => i.status !== 'pending');
  
  // Group pending issues by collaborator
  const pendingByCollaborator = pendingIssues.reduce((acc, issue) => {
    const key = issue.collaborator_id || issue.collaborator_name || 'unknown';
    if (!acc[key]) {
      acc[key] = {
        collaborator_id: issue.collaborator_id,
        collaborator_name: issue.collaborator_name,
        issues: [],
      };
    }
    acc[key].issues.push(issue);
    return acc;
  }, {});
  const pendingGroups = Object.values(pendingByCollaborator).sort(
    (a, b) => b.issues.length - a.issues.length
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg font-medium text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-4xl font-heading font-bold tracking-tight leading-none">
          Signalements d'outils
        </h1>
        <p className="text-muted-foreground mt-2">
          Demandes de remplacement et fin de leasing signalées par les collaborateurs
        </p>
      </div>

      <Tabs defaultValue="pending" className="space-y-6">
        <TabsList className="grid w-full max-w-2xl grid-cols-4">
          <TabsTrigger value="pending" data-testid="tab-pending" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            En attente ({pendingIssues.length})
          </TabsTrigger>
          <TabsTrigger value="leasing" data-testid="tab-leasing" className="flex items-center gap-2">
            <Truck className="w-4 h-4" />
            À retourner ({leasingReturns.length})
          </TabsTrigger>
          <TabsTrigger value="defective" data-testid="tab-defective">
            <Package className="w-4 h-4 mr-1" />
            Défectueux ({defectiveTools.length})
          </TabsTrigger>
          <TabsTrigger value="processed" data-testid="tab-processed">
            Historique ({processedIssues.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          {pendingGroups.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pendingGroups.map((group) => {
                const isMulti = group.issues.length > 1;
                const key = group.collaborator_id || group.collaborator_name;
                
                if (!isMulti) {
                  const issue = group.issues[0];
                  return (
                    <Card
                      key={issue.id}
                      data-testid={`issue-card-${issue.id}`}
                      className={`hover:shadow-lg transition-shadow duration-200 ${
                        issue.issue_type === 'leasing_end' ? 'border-blue-200' : 'border-orange-200'
                      }`}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <CardTitle className="text-lg font-heading flex items-center gap-2">
                              <Wrench className="w-5 h-5" />
                              {issue.tool_name}
                            </CardTitle>
                            {getIssueTypeBadge(issue.issue_type)}
                          </div>
                          {getStatusBadge(issue.status)}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center gap-2 text-sm">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Signalé par:</span>
                          <span className="font-medium">{issue.collaborator_name}</span>
                        </div>
                        {issue.description && (
                          <div className="text-sm bg-muted p-3 rounded-lg">
                            <p className="text-muted-foreground">{issue.description}</p>
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground">
                          {formatDate(issue.created_at)}
                        </div>
                        {user?.role === 'admin' && (
                          <div className="flex gap-2 pt-2">
                            <Button
                              data-testid={`process-issue-${issue.id}`}
                              onClick={() => openProcessDialog(issue)}
                              variant="default"
                              size="sm"
                              className="flex-1 rounded-full"
                            >
                              Traiter
                            </Button>
                            <Button
                              data-testid={`delete-issue-${issue.id}`}
                              onClick={() => handleDelete(issue.id)}
                              variant="destructive"
                              size="sm"
                              className="rounded-full"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                }
                
                // Grouped card for multiple issues from same collaborator
                return (
                  <Card
                    key={key}
                    data-testid={`issue-group-${key}`}
                    className="hover:shadow-lg transition-shadow duration-200 border-2 border-orange-300 bg-orange-50/30 md:col-span-2 lg:col-span-1"
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <User className="w-5 h-5 text-orange-600 shrink-0" />
                          <div className="min-w-0">
                            <CardTitle className="text-lg font-heading truncate">
                              {group.collaborator_name}
                            </CardTitle>
                            <p className="text-xs text-muted-foreground">
                              Signalements groupés
                            </p>
                          </div>
                        </div>
                        <Badge variant="destructive" className="shrink-0">
                          {group.issues.length} outils
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {group.issues
                        .slice()
                        .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
                        .map((issue) => (
                          <div
                            key={issue.id}
                            data-testid={`grouped-issue-${issue.id}`}
                            className="p-3 bg-white rounded-lg border border-orange-100 space-y-2"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Wrench className="w-4 h-4 text-muted-foreground shrink-0" />
                                  <span className="font-medium truncate">{issue.tool_name}</span>
                                  {getIssueTypeBadge(issue.issue_type)}
                                </div>
                                {issue.description && (
                                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                    {issue.description}
                                  </p>
                                )}
                                <p className="text-[10px] text-muted-foreground mt-1">
                                  {formatDate(issue.created_at)}
                                </p>
                              </div>
                              {user?.role === 'admin' && (
                                <div className="flex flex-col gap-1 shrink-0">
                                  <Button
                                    data-testid={`process-issue-${issue.id}`}
                                    onClick={() => openProcessDialog(issue)}
                                    variant="default"
                                    size="sm"
                                    className="h-7 px-3 text-xs rounded-full"
                                  >
                                    Traiter
                                  </Button>
                                  <Button
                                    data-testid={`delete-issue-${issue.id}`}
                                    onClick={() => handleDelete(issue.id)}
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 rounded-full"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="border-2 border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle className="w-12 h-12 text-chart-3 mb-4" />
                <p className="text-muted-foreground">Aucun signalement en attente</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* NEW TAB: Leasing Returns - Ready to return to supplier */}
        <TabsContent value="leasing" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-heading font-bold flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-600" />
              Prêt à retourner au fournisseur
            </h2>
            <Badge variant="outline" className="border-blue-500 text-blue-600">
              {leasingReturns.length} outil(s)
            </Badge>
          </div>
          
          {leasingReturns.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {leasingReturns.map((item) => (
                <Card
                  key={item.id}
                  data-testid={`leasing-return-card-${item.id}`}
                  className="hover:shadow-lg transition-shadow duration-200 border-blue-200"
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <CardTitle className="text-lg font-heading flex items-center gap-2">
                          <Wrench className="w-5 h-5" />
                          {item.tool_name}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">{item.tool_type}</p>
                      </div>
                      <Badge variant="outline" className="border-blue-500 text-blue-600 bg-blue-50">
                        <Truck className="w-3 h-3 mr-1" />
                        Fin de leasing
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-sm space-y-2">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Rendu par:</span>
                        <span className="font-medium">{item.collaborator_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Fournisseur:</span>
                        <span className="font-medium">{item.supplier || 'N/A'}</span>
                      </div>
                      <div className="text-muted-foreground">
                        N° Article: {item.article_number}
                      </div>
                      <div className="text-muted-foreground">
                        Quantité: {item.quantity}
                      </div>
                    </div>

                    {item.description && (
                      <div className="text-sm bg-muted p-2 rounded-lg">
                        <p className="text-muted-foreground">{item.description}</p>
                      </div>
                    )}

                    <div className="text-xs text-muted-foreground space-y-1">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Signalé: {formatDate(item.reported_date)}
                      </div>
                      <div className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Approuvé: {formatDate(item.processed_date)}
                      </div>
                    </div>

                    {user?.role === 'admin' && (
                      <Button
                        data-testid={`complete-return-${item.id}`}
                        onClick={() => handleCompleteReturn(item.id)}
                        variant="default"
                        size="sm"
                        className="w-full rounded-full mt-2 bg-blue-600 hover:bg-blue-700"
                      >
                        <Truck className="w-4 h-4 mr-2" />
                        Confirmer la restitution
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-2 border-dashed border-blue-200">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Truck className="w-12 h-12 text-blue-400 mb-4" />
                <p className="text-muted-foreground">Aucun outil à retourner au fournisseur</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="defective" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-heading font-bold">Outils Perdus / Cassés / Usés</h2>
            <Badge variant="outline">{defectiveTools.length} outil(s)</Badge>
          </div>
          
          {defectiveTools.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {defectiveTools.map((item) => (
                <Card
                  key={item.id}
                  data-testid={`defective-card-${item.id}`}
                  className="hover:shadow-lg transition-shadow duration-200"
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <CardTitle className="text-lg font-heading flex items-center gap-2">
                          <Wrench className="w-5 h-5" />
                          {item.tool_name}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">{item.tool_type}</p>
                      </div>
                      {getIssueTypeBadge(item.issue_type)}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-sm space-y-1">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Collaborateur:</span>
                        <span className="font-medium">{item.collaborator_name}</span>
                      </div>
                      <div className="text-muted-foreground">
                        N° Article: {item.article_number}
                      </div>
                      <div className="text-muted-foreground">
                        Quantité: {item.quantity}
                      </div>
                    </div>

                    {item.description && (
                      <div className="text-sm bg-muted p-2 rounded-lg">
                        <p className="text-muted-foreground">{item.description}</p>
                      </div>
                    )}

                    {item.replacement_tool_name && (
                      <div className="flex items-center gap-2 text-sm bg-chart-3/10 p-2 rounded-lg">
                        <ArrowRight className="w-4 h-4 text-chart-3" />
                        <span className="text-muted-foreground">Remplacé par:</span>
                        <span className="font-medium text-chart-3">{item.replacement_tool_name}</span>
                      </div>
                    )}

                    <div className="text-xs text-muted-foreground space-y-1">
                      <div>Signalé: {formatDate(item.reported_date)}</div>
                      <div>Traité: {formatDate(item.processed_date)}</div>
                    </div>

                    {user?.role === 'admin' && (
                      <Button
                        data-testid={`delete-defective-${item.id}`}
                        onClick={() => handleDeleteDefective(item.id)}
                        variant="outline"
                        size="sm"
                        className="w-full rounded-full mt-2"
                      >
                        <Trash2 className="w-3 h-3 mr-2" />
                        Supprimer de la liste
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-2 border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle className="w-12 h-12 text-chart-3 mb-4" />
                <p className="text-muted-foreground">Aucun outil défectueux enregistré</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="processed" className="space-y-4">
          {processedIssues.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {processedIssues.map((issue) => (
                <Card
                  key={issue.id}
                  data-testid={`issue-card-${issue.id}`}
                  className="hover:shadow-lg transition-shadow duration-200 opacity-75"
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <CardTitle className="text-lg font-heading flex items-center gap-2">
                          <Wrench className="w-5 h-5" />
                          {issue.tool_name}
                        </CardTitle>
                        {getIssueTypeBadge(issue.issue_type)}
                      </div>
                      {getStatusBadge(issue.status)}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{issue.collaborator_name}</span>
                    </div>

                    {issue.description && (
                      <div className="text-sm bg-muted p-3 rounded-lg">
                        <p className="text-muted-foreground">{issue.description}</p>
                      </div>
                    )}

                    {issue.admin_notes && (
                      <div className="text-sm bg-primary/10 p-3 rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Note admin:</p>
                        <p>{issue.admin_notes}</p>
                      </div>
                    )}

                    <div className="text-xs text-muted-foreground space-y-1">
                      <div>Signalé: {formatDate(issue.created_at)}</div>
                      {issue.processed_at && (
                        <div>Traité: {formatDate(issue.processed_at)}</div>
                      )}
                    </div>

                    {user?.role === 'admin' && (
                      <Button
                        data-testid={`delete-issue-${issue.id}`}
                        onClick={() => handleDelete(issue.id)}
                        variant="outline"
                        size="sm"
                        className="w-full rounded-full"
                      >
                        <Trash2 className="w-3 h-3 mr-2" />
                        Supprimer
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-2 border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <AlertCircle className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Aucun signalement dans l'historique</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Process Dialog */}
      <Dialog open={processDialogOpen} onOpenChange={(open) => {
        setProcessDialogOpen(open);
        if (!open) {
          setSelectedIssue(null);
          setAdminNotes('');
          setSelectedReplacementTool('');
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Traiter le signalement</DialogTitle>
          </DialogHeader>
          {selectedIssue && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-lg font-heading font-medium">{selectedIssue.tool_name}</p>
                  {getIssueTypeBadge(selectedIssue.issue_type)}
                </div>
                <p className="text-sm text-muted-foreground">
                  Signalé par: <span className="font-medium">{selectedIssue.collaborator_name}</span>
                </p>
                {selectedIssue.description && (
                  <p className="text-sm mt-2 pt-2 border-t">{selectedIssue.description}</p>
                )}
              </div>

              {/* Show replacement tool selection only for non-leasing issues */}
              {selectedIssue.issue_type !== 'leasing_end' && (
                <div className="space-y-2">
                  <Label htmlFor="replacement_tool">Outil de remplacement *</Label>
                  <Select
                    value={selectedReplacementTool}
                    onValueChange={setSelectedReplacementTool}
                  >
                    <SelectTrigger id="replacement_tool" data-testid="replacement-tool-select" className="rounded-lg">
                      <SelectValue placeholder="Sélectionner un outil de remplacement" />
                    </SelectTrigger>
                    <SelectContent>
                      {tools.filter(t => t.quantity_available > 0).map((tool) => (
                        <SelectItem key={tool.id} value={tool.id}>
                          {tool.name} - {tool.type} ({tool.quantity_available} dispo.)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    L'outil défectueux sera retiré et le nouveau sera automatiquement affecté au collaborateur.
                  </p>
                </div>
              )}

              {/* Info for leasing end */}
              {selectedIssue.issue_type === 'leasing_end' && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-700 font-medium mb-2">
                    <Truck className="w-5 h-5" />
                    Fin de leasing
                  </div>
                  <p className="text-sm text-blue-600">
                    En approuvant ce signalement, l'outil sera retiré de l'inventaire du collaborateur 
                    et ajouté à la liste "Prêt à retourner au fournisseur".
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="admin_notes">Note (optionnel)</Label>
                <Textarea
                  id="admin_notes"
                  data-testid="admin-notes-input"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Ajouter une note sur le traitement..."
                  className="rounded-lg"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  data-testid="reject-issue-button"
                  onClick={() => handleProcess('rejected')}
                  variant="outline"
                  className="flex-1 rounded-full"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Rejeter
                </Button>
                <Button
                  data-testid="approve-issue-button"
                  onClick={() => handleProcess('processed')}
                  className={`flex-1 rounded-full ${selectedIssue.issue_type === 'leasing_end' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                  disabled={selectedIssue.issue_type !== 'leasing_end' && !selectedReplacementTool}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {selectedIssue.issue_type === 'leasing_end' ? 'Approuver' : 'Approuver et Remplacer'}
                </Button>
              </div>

              {selectedIssue.issue_type !== 'leasing_end' && (
                <div className="text-xs text-muted-foreground text-center p-2 bg-chart-3/10 rounded-lg">
                  <strong>Que se passe-t-il à l'approbation ?</strong>
                  <br />
                  1. L'outil défectueux est retiré de l'inventaire du collaborateur
                  <br />
                  2. Le nouvel outil est automatiquement affecté
                  <br />
                  3. L'outil est ajouté à la liste "Défectueux" pour suivi
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        onConfirm={confirmDelete}
        title="Supprimer le signalement"
        description="Êtes-vous sûr de vouloir supprimer ce signalement ? Cette action est irréversible."
      />

      <ConfirmDialog
        open={confirmDeleteDefectiveOpen}
        onOpenChange={setConfirmDeleteDefectiveOpen}
        onConfirm={confirmDeleteDefective}
        title="Supprimer de la liste"
        description="Êtes-vous sûr de vouloir supprimer cet outil de la liste des défectueux ? Cette action est irréversible et ne modifie pas l'inventaire."
      />

      <ConfirmDialog
        open={confirmReturnOpen}
        onOpenChange={setConfirmReturnOpen}
        onConfirm={confirmCompleteReturn}
        title="Confirmer la restitution au fournisseur"
        description="Confirmez-vous que cet outil a été retourné au fournisseur ? Cette action sera enregistrée dans l'historique avec le nom du collaborateur et la date."
        confirmText="Confirmer la restitution"
      />
    </div>
  );
};

export default ToolIssues;
